// src/components/doctor/ConsultationPanel.jsx
//
// The doctor's consultation on the appointment page, composed from presentational
// pieces (all state and side effects live in hooks/useConsultation.js):
//
//   Tier 1  ConsultHeader (sticky) + GlanceStrip   always visible
//   Tier 2  VisitStepper + one of four steps       Assessment · Prescription ·
//                                                  Orders & follow-up · Review & sign
//   Tier 3  ContextDrawer                          Attention · Report · History · Documents
//   Tier 4  ⋯ menu → AppointmentDetailsDialog, close-out confirmations
//
// The open step is the `?step=` query param (replace, so refresh keeps it).
// PrescriptionBuilder stays mounted (hidden) off its own step because it holds
// the medicine rows the sign sequence saves. Signing runs from the header OR the
// Review step through the same confirm dialog (useConsultation.doSign).

import VitalsIntakeForm from '../VitalsIntakeForm';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useToast } from '../../context/ToastContext';
import { doctorAppointmentAPI } from '../../services/doctorApi';
import useConsultation from '../../hooks/useConsultation';
import useMediaQuery from '../../hooks/useMediaQuery';
import { attentionSummary } from '../../utils/actionItems';
import {
  STEPS, parseStepParam, stepStates, stepSubtitles, reviewRows, orderSummaryText, nextStepKey, prevStepKey, stepIndex,
} from '../../utils/consultSteps';
import { labOrderFromSteps, instructionSteps } from '../../utils/labOrders';
import { CLOSED_STATUSES } from '../../utils/appointmentDisplay';
import ConsultHeader from './ConsultHeader';
import GlanceStrip from './GlanceStrip';
import VisitStepper from './VisitStepper';
import AssessmentStep from './AssessmentStep';
import OrdersStep from './OrdersStep';
import ReviewStep from './ReviewStep';
import PrescriptionBuilder from './PrescriptionBuilder';
import ContextDrawer from './ContextDrawer';
import ConfirmSignDialog from './ConfirmSignDialog';
import ConfirmActionDialog from './ConfirmActionDialog';
import AppointmentDetailsDialog from './AppointmentDetailsDialog';

const DRAWER_KEY = 'previu.consult.drawer';
const readDrawer = () => {
  try { const v = JSON.parse(sessionStorage.getItem(DRAWER_KEY) || 'null'); return v && typeof v === 'object' ? v : {}; } catch { return {}; }
};
const writeDrawer = v => { try { sessionStorage.setItem(DRAWER_KEY, JSON.stringify(v)); } catch { /* storage unavailable */ } };

const CLOSE_COPY = {
  COMPLETED: {
    title: 'Mark complete without a summary?',
    body: 'The appointment is closed as completed, but no diagnosis, advice or prescription is signed or sent to the patient. Use Sign & complete instead if you want them to have a summary.',
    confirm: 'Mark complete', busy: 'Closing…', done: 'Visit marked complete',
  },
  NO_SHOW: {
    title: 'Mark the patient as a no-show?',
    body: 'The appointment is closed as "no show". The server only allows this for patients who have not arrived.',
    confirm: 'Mark as no-show', busy: 'Updating…', done: 'Marked as no-show',
  },
};

function Gate({ status, onStart, onOpen }) {
  return (
    <div className="card fade-in-up delay-2" data-testid="consultation-gate">
      <div className="card-title">Consultation</div>
      {status === 'ARRIVED' ? (
        <>
          <div className="field-text" style={{ marginBottom: 12 }}>This patient has checked in and is waiting.</div>
          <button type="button" className="btn btn-accent" onClick={onStart}>Call in &amp; start consultation</button>
        </>
      ) : (
        <>
          <div className="field-text" style={{ marginBottom: 12 }}>This visit is {status.replace(/_/g, ' ').toLowerCase()}.</div>
          <button type="button" className="btn btn-secondary" onClick={onOpen}>Open visit summary</button>
        </>
      )}
    </div>
  );
}

export default function ConsultationPanel({ appointment, patientId, patientName, patientPhone, onStatusChange }) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const c = useConsultation({ appointment, patientId, onStatusChange });
  const status = appointment.status;
  const narrow = useMediaQuery('(max-width: 900px)');

  // ── Step (URL) ─────────────────────────────────────────────────────────────
  const step = parseStepParam(searchParams);
  const setStep = useCallback(key => {
    setSearchParams(prev => { const p = new URLSearchParams(prev); p.set('step', key); return p; }, { replace: true });
  }, [setSearchParams]);
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) { firstRender.current = false; return; }
    document.getElementById('consult-step-title')?.focus();
  }, [step]);

  // ── Drawer ─────────────────────────────────────────────────────────────────
  const [drawer, setDrawer] = useState(() => {
    const d = readDrawer();
    // A bottom sheet is modal: never restore it open on load.
    return { open: !!d.open && !narrow, tab: ['attention', 'report', 'history', 'documents'].includes(d.tab) ? d.tab : 'attention' };
  });
  const updateDrawer = useCallback(next => { setDrawer(next); writeDrawer(next); }, []);
  const openDrawer = tab => updateDrawer({ open: true, tab: tab || drawer.tab });
  // "View" on the review step. If the drawer is already open on Attention there is nothing
  // to open, so bring it into view, focus it and flash it — otherwise the click looks dead.
  const viewOpenItems = () => {
    const wasShowing = drawer.open && drawer.tab === 'attention';
    openDrawer('attention');
    if (!wasShowing) return;
    const el = document.querySelector('[data-testid="context-drawer"]');
    if (!el) return;
    el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    el.querySelector('[role="tab"][aria-selected="true"]')?.focus({ preventScroll: true });
    el.classList.remove('consult-drawer--flash');
    void el.offsetWidth; // restart the animation on repeat clicks
    el.classList.add('consult-drawer--flash');
    setTimeout(() => el.classList.remove('consult-drawer--flash'), 1400);
  };
  const closeDrawer = useCallback(() => updateDrawer({ ...drawer, open: false }), [drawer, updateDrawer]);

  // ── Dialogs / close-out ────────────────────────────────────────────────────
  const [showDetails, setShowDetails] = useState(false);
  const [recordingVitals, setRecordingVitals] = useState(false);
  const [closing, setClosing] = useState(null);       // 'COMPLETED' | 'NO_SHOW' | null
  const [closeBusy, setCloseBusy] = useState(false);
  const [closeProblem, setCloseProblem] = useState(null);
  const openRecord = useCallback(() => navigate(`/doctor/patients/${patientId}`), [navigate, patientId]);

  const closeVisit = async () => {
    setCloseBusy(true); setCloseProblem(null);
    try {
      await doctorAppointmentAPI.updateStatus(appointment.id, closing);
      onStatusChange?.(closing);
      toast.success(CLOSE_COPY[closing].done);
      setClosing(null);
    } catch (err) {
      const msg = err?.response?.data?.message;
      setCloseProblem(err?.response?.status === 403
        ? 'You can only close out your own appointments'
        : (typeof msg === 'string' && msg) || "Couldn't update the visit — please try again");
    } finally { setCloseBusy(false); }
  };
  const askClose = s => { setCloseProblem(null); setClosing(s); };

  // ── Derived view data ──────────────────────────────────────────────────────
  const { form } = c;
  const attention = useMemo(() => attentionSummary(c.context.items, c.context.reports[0]), [c.context]);
  const medicineCount = c.rxItems.filter(i => i.drugName.trim()).length;
  const done = { form, medicineCount, noMedicines: c.noMedicines, signed: c.locked };
  const states = stepStates(done);
  const subtitles = stepSubtitles(done);
  const labTests = labOrderFromSteps(form.nextSteps).markers;
  const instructionCount = instructionSteps(form.nextSteps).filter(({ step: s }) => s.description.trim()).length;
  const rows = reviewRows({
    form, medicineCount, medicineSummary: c.medicineSummary, noMedicines: c.noMedicines, allergies: c.allergies,
    uncheckedUrgent: attention.urgentCount,
    orderSummary: orderSummaryText({ tests: labTests, instructionCount, followUpWeeks: form.followUpWeeks }),
  });

  const headerProps = {
    appointment, patientName, age: c.patient.age, gender: c.patient.gender,
    token: c.queueEntry?.token || appointment.queueToken,
    consultationStartedAt: appointment.consultationStartedAt || c.queueEntry?.consultationStartedAt,
    onDetails: () => setShowDetails(true), onRecord: patientId ? openRecord : undefined, onCloseVisit: askClose,
  };
  const dialogs = (
    <>
      {showDetails && (
        <AppointmentDetailsDialog appointment={appointment} patientName={patientName} patientPhone={patientPhone}
          onClose={() => setShowDetails(false)} onOpenRecord={patientId ? openRecord : undefined} />
      )}
      {closing && (
        <ConfirmActionDialog title={CLOSE_COPY[closing].title} confirmLabel={CLOSE_COPY[closing].confirm} busyLabel={CLOSE_COPY[closing].busy}
          busy={closeBusy} problem={closeProblem} onCancel={() => setClosing(null)} onConfirm={closeVisit}>
          {CLOSE_COPY[closing].body}
        </ConfirmActionDialog>
      )}
    </>
  );

  // ── Not a consultation ─────────────────────────────────────────────────────
  if (status === 'CANCELLED' || status === 'NO_SHOW') {
    return (
      <div>
        <ConsultHeader {...headerProps} />
        <div className="card"><div className="field-text">This appointment is {status === 'NO_SHOW' ? 'marked as a no-show' : 'cancelled'}; there is no consultation to run.</div></div>
        {dialogs}
      </div>
    );
  }
  if (c.phase === 'idle') {
    return (
      <div>
        <ConsultHeader {...headerProps} />
        <Gate status={status} onStart={() => c.openVisit({ start: true })} onOpen={() => c.openVisit()} />
        {dialogs}
      </div>
    );
  }
  if (c.phase === 'loading') {
    return <div><ConsultHeader {...headerProps} /><div className="card"><div className="card-title">Consultation</div><div className="skeleton skeleton--body" /></div>{dialogs}</div>;
  }
  if (c.phase === 'error') {
    return (
      <div>
        <ConsultHeader {...headerProps} />
        <div className="card"><div className="card-title">Consultation</div>
          <div className="alert alert-error"><div className="grow">{c.loadError}</div>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => c.openVisit()}>Retry</button></div>
        </div>
        {dialogs}
      </div>
    );
  }
  // Closed from the ⋯ menu without signing: nothing left to edit.
  if (CLOSED_STATUSES.includes(status) && !c.locked) {
    return (
      <div>
        <ConsultHeader {...headerProps} />
        <div className="alert alert-info"><div className="grow">This visit was closed without a summary, so nothing was signed or sent to the patient.</div></div>
        {dialogs}
      </div>
    );
  }

  const locked = c.locked;
  const canSign = !c.diagnosisBlank;
  const goNext = () => setStep(nextStepKey(step));
  const goBack = () => setStep(prevStepKey(step));
  const nextLabel = STEPS[stepIndex(nextStepKey(step))].short === 'Sign' ? 'Review & sign' : STEPS[stepIndex(nextStepKey(step))].label;

  const ctx = {
    context: c.context, toggleItem: c.toggleItem, onOpenRecord: openRecord, history: c.history, loadHistory: c.loadHistory,
    previousRx: c.previousRx, allRx: c.allRx, locked, copyMedicines: c.copyMedicines, patientId,
  };

  return (
    <div data-testid="consultation-panel">
      <ConsultHeader {...headerProps} showWork signed={locked} saveState={c.saveState} savedAt={c.savedAt} onRetrySave={c.saveDraft}
        canSign={canSign} signing={c.signing} onSign={c.askToSign}
        onContext={() => (drawer.open ? closeDrawer() : openDrawer())} contextOpen={drawer.open} />
      <GlanceStrip appointment={appointment} visit={c.visit} vitals={c.vitals} allergies={c.allergies} attention={attention}
        onRecordVitals={c.locked || !patientId ? undefined : () => setRecordingVitals(true)}
        contextStatus={c.context.status} onOpenAttention={() => (drawer.open && drawer.tab === 'attention' ? closeDrawer() : openDrawer('attention'))} />
      <VisitStepper current={step} states={states} subtitles={subtitles} onSelect={setStep} />

      <div className={`consult${drawer.open ? ' consult--drawer' : ''}`}>
        <div className="consult-main">
          {locked && (
            <div className="alert alert-success" style={{ marginBottom: 12 }}>
              <div className="grow">Signed {c.visit.signedAt ? new Date(c.visit.signedAt).toLocaleString('en-IN') : ''}. This visit is now read-only.</div>
              {patientId && (
                <button type="button" className="consult-link" onClick={() => navigate(`/doctor/patients/${patientId}?tab=Visits${c.visitId ? `&visit=${c.visitId}` : ''}`)}>
                  View in patient’s visits →
                </button>
              )}
            </div>
          )}

          {step === 'assessment' && (
            <AssessmentStep form={form} setField={c.setField} locked={locked} notes={c.visitNotes} visitTagLength={c.visitTag.length}
              noteDraft={c.noteDraft} setNoteDraft={c.setNoteDraft} noteSaving={c.noteSaving} onSaveNote={c.saveNote} onNext={goNext} />
          )}

          <div hidden={step !== 'prescription'}>
            {c.rxList === null ? (
              <div className="card"><div className="card-title">Prescription</div><div className="skeleton skeleton--body" /></div>
            ) : (
              <PrescriptionBuilder ref={c.builderRef} key={c.visitId} patientId={patientId} visitId={c.visitId}
                diagnosisText={form.diagnosisText} advice={form.adviceForPatient} allergies={c.allergies}
                initial={c.rxInitial} readOnly={locked} onItemsChange={c.setRxItems}
                noMedicines={c.noMedicines} onNoMedicinesChange={c.setNoMedicines} active={step === 'prescription'} />
            )}
            <div className="consult-foot" style={{ borderTop: 0, marginTop: 0, paddingTop: 0, marginBottom: 14 }}>
              <button type="button" className="btn btn-ghost" onClick={goBack}>← Back</button>
              <button type="button" className="btn btn-primary" onClick={goNext}>Continue: Orders &amp; follow-up →</button>
            </div>
          </div>

          {step === 'orders' && (
            <OrdersStep patientId={patientId} form={form} setField={c.setField} setNextSteps={c.setNextSteps} locked={locked}
              onBack={goBack} onNext={goNext} />
          )}

          {step === 'review' && (
            <ReviewStep rows={rows} patientName={patientName} canSign={canSign} locked={locked} signing={c.signing} saveState={c.saveState}
              signProblem={c.signProblem} confirming={c.confirming} onEdit={setStep} onViewContext={viewOpenItems} onBack={goBack} onSign={c.askToSign} />
          )}

          <div className="consult-bottombar">
            <button type="button" className="btn btn-secondary" style={{ flex: 1 }} onClick={() => (drawer.open ? closeDrawer() : openDrawer())}
              aria-expanded={drawer.open}>Context{attention.urgentCount > 0 ? ` (${attention.urgentCount})` : ''} ▴</button>
            {!locked && step !== 'review' && <button type="button" className="btn btn-primary" style={{ flex: 2 }} onClick={goNext}>Next: {nextLabel} →</button>}
            {!locked && step === 'review' && <button type="button" className="btn btn-accent" style={{ flex: 2 }} disabled={!canSign || c.signing} onClick={c.askToSign}>Sign &amp; complete</button>}
          </div>
        </div>

        <ContextDrawer open={drawer.open} modal={narrow} tab={drawer.tab} onTab={tab => updateDrawer({ open: true, tab })} onClose={closeDrawer}
          attentionCount={attention.urgentCount} ctx={ctx} />
      </div>

      {recordingVitals && (
        <VitalsIntakeForm patientId={patientId} patientName={patientName} appointmentId={appointment.id}
          onClose={() => setRecordingVitals(false)} onSaved={() => { c.reloadVitals(); }} />
      )}

      {/* The header button and the Review button both end here. */}
      {c.confirming && (
        <ConfirmSignDialog busy={c.signing} diagnosis={form.diagnosisText} problem={c.signProblem}
          onCancel={() => c.setConfirming(false)} onConfirm={c.doSign} />
      )}
      {dialogs}
    </div>
  );
}
